import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import {
  fetchOperationalCapabilities,
  fetchOperationalOverview,
  fetchOperationalWindow,
  getOperationalRecording,
  operationalKeys,
} from "@rekonas/data-access";
import { SignalViewer, profileWithOrientationModels, type ViewerDataSource } from "@rekonas/signal-viewer";
import { Alert, Button, Spinner } from "@rekonas/ui";
import { useOperationalWorkspace } from "../operationalWorkspace";
import { productStatus } from "./RecordingsPage";

export function RecordingDetailPage() {
  const { recordingRef = "" } = useParams();
  const workspaces = useOperationalWorkspace();
  const workspaceRef = workspaces.workspace?.slug || "";
  const query = useQuery({
    queryKey: operationalKeys.detail(workspaceRef, recordingRef),
    queryFn: ({ signal }) => getOperationalRecording(workspaceRef, recordingRef, signal),
    enabled: Boolean(workspaceRef && recordingRef),
    refetchInterval: (state) => state.state.data && state.state.data.state !== "ready" && state.state.data.state !== "attention_required" ? 5_000 : false,
  });
  const recording = query.data;
  const source = useMemo<ViewerDataSource | null>(() => {
    if (!recording?.profile || !recording.data_version || !recording.profile_sha256 || !recording.duration_seconds) return null;
    const pin = { workspaceRef, recordingRef: recording.recording_ref,
      dataVersion: recording.data_version, profileSha256: recording.profile_sha256 };
    return {
      key: operationalKeys.signalScope(workspaceRef, recording.recording_ref, recording.data_version),
      recordingRef: recording.recording_ref,
      durationSeconds: recording.duration_seconds,
      profile: profileWithOrientationModels(recording.profile),
      loadCapabilities: (signal) => fetchOperationalCapabilities({ ...pin, signal }),
      loadOverview: (modalities, maxPoints, signal) => fetchOperationalOverview({ ...pin, modalities, maxPoints, signal }),
      loadWindow: (modalities, startSeconds, durationSeconds, signal) => fetchOperationalWindow({
        ...pin, modalities, startSeconds, durationSeconds, signal,
      }),
    };
  }, [recording, workspaceRef]);

  useEffect(() => { document.title = recording ? `Night recording · Rekonas` : "Recording · Rekonas"; }, [recording]);

  if (workspaces.isPending || query.isPending) return <main className="operational-loading"><Spinner /> Loading recording…</main>;
  if (workspaces.isError || workspaces.authorizedCount !== 1 || query.isError || !recording) return (
    <main className="operational-main"><Alert className="recording-list-alert">
      <h1>Recording could not be opened.</h1>
      <p>It may not exist in this authorized workspace.</p>
      <Link to="/recordings">Return to recordings</Link>
    </Alert></main>
  );

  const status = productStatus(recording);
  const date = recording.display_date ? new Intl.DateTimeFormat(undefined, { dateStyle: "full", timeStyle: "short" }).format(new Date(recording.display_date)) : "Date pending";
  const profile = recording.profile;
  const recordingDuration = recording.duration_seconds;
  return (
    <main className="operational-main operational-viewer-main">
      <Link className="operational-back" to="/recordings"><span aria-hidden="true">←</span> All recordings</Link>
      <section className="recording-detail-header">
        <div><p>Night recording</p><h1>{date}</h1><span>{recording.recording_ref}</span></div>
        <div className={`product-status status-${status.tone}`}>{status.label}</div>
        <Button size="sm" variant="ghost" onClick={() => void query.refetch()} disabled={query.isFetching}>{query.isFetching && <Spinner />} Refresh</Button>
      </section>
      {!status.viewer || !source ? (
        <section className="processing-recording-state">
          <p>Signal processing</p><h2>{recording.signals === "failed" ? "Signals unavailable" : "This recording is still being prepared"}</h2>
          <span>{recording.signals === "failed"
            ? "Signal processing failed. No scientific data is presented as ready."
            : "The recording remains visible and this page will update while processing continues."}</span>
        </section>
      ) : (
        <SignalViewer
          attention={recording.hypnogram === "failed" ? "Signals are available. Sleep analysis failed, so Sleep stage remains unavailable for this version." : null}
          dataSource={source}
          provenance={<div className="viewer-provenance-row">
            <span>Recording <strong>{recording.recording_ref}</strong></span>
            <span>Workspace <strong>{recording.workspace_ref}</strong></span>
            <span>Profile <strong>{profile?.profile_id} · {profile?.profile_version}</strong></span>
            <details><summary>View data identity</summary><div>
              <p>Portable recording reference: {recording.recording_ref}</p>
              <p>Data version: {recording.data_version}</p>
              <p>Signal profile SHA-256: {recording.profile_sha256}</p>
            </div></details>
          </div>}
          qualityLabel={recording.device_summary.join(" · ") || "Device metadata pending"}
          summary={<dl className="operational-summary-strip">
            <div><dt>Night</dt><dd>{date}</dd></div>
            <div><dt>Duration</dt><dd>{recordingDuration ? `${Math.floor(recordingDuration / 3600)}h ${Math.floor((recordingDuration % 3600) / 60)}m` : "Pending"}</dd></div>
            <div><dt>Signals</dt><dd>{recording.signals === "available" ? "Available" : recording.signals}</dd></div>
            <div><dt>Sleep analysis</dt><dd>{recording.hypnogram === "available" ? "Available" : recording.hypnogram}</dd></div>
          </dl>}
        />
      )}
    </main>
  );
}
